import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  Building2,
  Users,
  HardHat,
  Layers,
  Wallet,
  Bell,
  FileText,
  ShieldCheck,
  Compass,
} from "lucide-react";
import DemoRequestModal from "@/components/DemoRequestModal";
import SavvyLogo from "@/components/SavvyLogo";

const MODULES = [
  { icon: Building2, title: "Tenant control", body: "Multi-tenant isolation across companies, projects, customers." },
  { icon: Layers, title: "Timeline updates", body: "Publish progress, milestones, site imagery in a structured feed." },
  { icon: Wallet, title: "Payment visibility", body: "Schedules, references, external invoice records — in one place." },
  { icon: Bell, title: "Alerts & tickets", body: "In-app plus email customer comms with a built-in support flow." },
  { icon: ShieldCheck, title: "Audit trail", body: "Every action logged by actor, entity, and tenant for compliance." },
  { icon: FileText, title: "Invoice intake", body: "Map external ERP customer IDs to in-app records automatically." },
];

const STEPS = [
  { n: "01", label: "Onboard", body: "Platform owner onboards a developer and sets plan limits." },
  { n: "02", label: "Configure", body: "Builder adds sites, projects, towers, units, and customers." },
  { n: "03", label: "Publish", body: "Site teams post updates, alerts, schedules — buyers get notified." },
  { n: "04", label: "Support", body: "Customers raise tickets; builders respond in a shared thread." },
];

const ROLES = [
  {
    icon: Compass,
    role: "Platform Owner",
    body: "Manage construction companies, plans, and manual billing lifecycle.",
    tone: "graphite",
  },
  {
    icon: HardHat,
    role: "Construction Company",
    body: "Run projects, publish updates, manage customers, ownerships, tickets, payments.",
    tone: "amber",
  },
  {
    icon: Users,
    role: "Customer",
    body: "See progress timelines, dues, invoices, and raise tickets — in one portal.",
    tone: "graphite",
  },
];

export default function Landing() {
  const [demoOpen, setDemoOpen] = useState(false);

  return (
    <div className="min-h-screen bg-concrete text-graphite" style={{ background: "var(--c-concrete)" }}>
      {/* Hazard tape — top thin strip */}
      <div className="h-1.5 hazard-tape" aria-hidden="true" />

      {/* Header */}
      <header className="border-b border-graphite/15 sticky top-0 z-40" style={{ background: "rgba(245,240,232,0.92)", backdropFilter: "blur(8px)" }}>
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <SavvyLogo variant="light" size={48} showTagline testId="header-logo" />
          <div className="flex items-center gap-5">
            <a href="#modules" data-testid="header-modules-link" className="text-sm font-medium text-graphite/80 hover:text-graphite hidden md:inline">
              Modules
            </a>
            <a href="#how" data-testid="header-how-link" className="text-sm font-medium text-graphite/80 hover:text-graphite hidden md:inline">
              How it works
            </a>
            <a href="#about" data-testid="header-about-link" className="text-sm font-medium text-graphite/80 hover:text-graphite hidden md:inline">
              About
            </a>
            <button
              onClick={() => setDemoOpen(true)}
              data-testid="header-demo-btn"
              className="text-sm font-bold text-graphite hover:text-brand-600 hidden sm:inline"
            >
              Request demo
            </button>
            <Link
              to="/login"
              data-testid="login-link"
              className="text-sm font-medium text-graphite/80 hover:text-graphite"
            >
              Sign in
            </Link>
            <Link
              to="/login"
              data-testid="get-started-btn"
              className="bg-graphite hover:bg-brand-600 text-white text-sm font-bold tracking-wide py-2.5 px-4 flex items-center gap-2 transition-colors"
            >
              Open Console <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-graphite/15">
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-14 md:pt-28 md:pb-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            {/* Project code slug — construction feel */}
            <div className="inline-flex items-center gap-3 font-mono text-[11px] tracking-[0.2em] text-rebar uppercase mb-8 border-l-2 border-amber-construction pl-3">
              <span className="text-amber-construction">●</span>
              File 01 / Product overview
            </div>

            <h1 className="font-display text-[3.25rem] md:text-7xl lg:text-[5.5rem] font-black tracking-[-0.03em] leading-[0.9] text-graphite">
              Build in the<br />
              open.<br />
              <span className="font-serif-accent text-rust">Close</span> on time.
            </h1>

            <p className="mt-8 max-w-xl text-lg leading-[1.7] text-graphite/70">
              Savvy is the operating layer between your site, your office, and your
              buyers. Publish milestones, schedule payments, and resolve customer
              tickets in one transparent, tenant-aware workspace.
            </p>

            <div className="mt-10 flex flex-wrap gap-3">
              <button
                onClick={() => setDemoOpen(true)}
                data-testid="hero-cta-demo"
                className="group bg-amber-construction hover:bg-[color:var(--c-amber-deep)] text-graphite font-bold text-sm tracking-wide uppercase py-4 px-6 flex items-center gap-3 transition-colors"
              >
                Request a demo
                <ArrowUpRight size={16} className="group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
              </button>
              <Link
                to="/login"
                data-testid="hero-cta-primary"
                className="bg-graphite hover:bg-brand-600 text-white font-bold text-sm tracking-wide uppercase py-4 px-6 flex items-center gap-3 transition-colors"
              >
                Launch console <ArrowRight size={14} />
              </Link>
            </div>

            {/* Signage-style stat rail */}
            <div className="mt-16 grid grid-cols-3 border-y-2 border-graphite" data-testid="hero-stats">
              {[
                { k: "24", v: "Stories live" },
                { k: "3", v: "User portals" },
                { k: "12+", v: "Core modules" },
              ].map((s, i) => (
                <div
                  key={s.v}
                  className={`py-5 pr-4 ${i > 0 ? "pl-6 border-l border-graphite/20" : ""}`}
                >
                  <div className="font-display font-black text-5xl text-graphite leading-none">{s.k}</div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-rebar mt-3 font-mono">
                    {s.v}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Hero visual */}
          <div className="lg:col-span-5">
            <div className="relative h-full min-h-[420px] border-2 border-graphite">
              <img
                alt="skyline cranes"
                src="https://images.pexels.com/photos/17815725/pexels-photo-17815725.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=940"
                className="w-full h-full object-cover absolute inset-0"
              />
              <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(28,25,23,0.15) 0%, rgba(28,25,23,0.75) 100%)" }} />

              {/* Header overlay */}
              <div className="absolute top-5 left-5 right-5 flex items-center justify-between text-white">
                <div className="font-mono text-[10px] tracking-[0.25em] uppercase">
                  Site / Horizon Heights
                </div>
                <div className="font-mono text-[10px] tracking-[0.15em] bg-amber-construction text-graphite px-2 py-1 font-bold">
                  45% COMPLETE
                </div>
              </div>

              {/* Update card */}
              <div className="absolute bottom-5 left-5 right-5 bg-concrete border-l-4 border-amber-construction p-5" data-testid="hero-update-card">
                <div className="text-[10px] uppercase tracking-[0.25em] text-amber-construction font-mono font-bold">
                  Latest update
                </div>
                <div className="font-display font-black text-xl mt-1.5 text-graphite leading-tight">
                  Structure reaches 12th floor
                </div>
                <div className="text-xs text-rebar mt-2 font-mono">
                  PUBLISHED 3 DAYS AGO · ALEX ROSS
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="bg-concrete-deep border-b border-graphite/15">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-amber-construction font-bold mb-4">
                File 02 / Method
              </div>
              <h2 className="font-display text-4xl md:text-5xl font-black tracking-[-0.02em] leading-[1.02] max-w-3xl text-graphite">
                From foundation to handover,
                <br />
                <span className="font-serif-accent text-rust">every step</span> in one place.
              </h2>
            </div>
            <div className="text-sm font-mono text-rebar max-w-xs">
              Built for the full project lifecycle — not just updates.
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border-t border-l border-graphite/20">
            {STEPS.map((s) => (
              <div
                key={s.n}
                data-testid={`step-${s.n}`}
                className="border-r border-b border-graphite/20 p-7 bg-concrete hover:bg-concrete-deep transition-colors group"
              >
                <div className="font-mono text-sm text-amber-construction font-bold tracking-widest">
                  {s.n}
                </div>
                <div className="font-display font-black text-2xl mt-3 text-graphite">
                  {s.label}
                </div>
                <div className="text-sm text-graphite/70 mt-3 leading-relaxed">
                  {s.body}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MODULES */}
      <section id="modules" className="border-b border-graphite/15">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-amber-construction font-bold mb-4">
                File 03 / Modules
              </div>
              <h2 className="font-display text-4xl md:text-5xl font-black tracking-[-0.02em] leading-[1.02] max-w-2xl text-graphite">
                Three portals.
                <br />
                <span className="font-serif-accent text-rust">One</span> source of truth.
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border-t border-l border-graphite/20">
            {MODULES.map((m) => (
              <div
                key={m.title}
                className="border-r border-b border-graphite/20 p-7 bg-concrete hover:bg-white transition-colors group"
                data-testid={`module-${m.title.replace(/\s/g, "-").toLowerCase()}`}
              >
                <div className="w-11 h-11 bg-graphite group-hover:bg-amber-construction flex items-center justify-center transition-colors">
                  <m.icon size={20} className="text-amber-construction group-hover:text-graphite transition-colors" />
                </div>
                <div className="font-display font-black text-xl mt-5 text-graphite">{m.title}</div>
                <p className="text-sm text-graphite/70 mt-2 leading-relaxed">{m.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ROLES */}
      <section className="bg-graphite text-white border-b border-graphite">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-amber-construction font-bold mb-4">
            File 04 / Portals
          </div>
          <h2 className="font-display text-4xl md:text-5xl font-black tracking-[-0.02em] leading-[1.02] max-w-3xl">
            Made for everyone on the{" "}
            <span className="font-serif-accent text-amber-construction">worksite</span>.
          </h2>

          <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-0 border-t border-l border-white/10">
            {ROLES.map((r) => (
              <div
                key={r.role}
                data-testid={`role-${r.role.replace(/\s/g, "-").toLowerCase()}`}
                className="border-r border-b border-white/10 p-8"
                style={{ background: r.tone === "amber" ? "var(--c-amber)" : "transparent", color: r.tone === "amber" ? "var(--c-graphite)" : "white" }}
              >
                <div
                  className="w-12 h-12 flex items-center justify-center border-2"
                  style={{
                    borderColor: r.tone === "amber" ? "var(--c-graphite)" : "var(--c-amber)",
                  }}
                >
                  <r.icon size={22} />
                </div>
                <div className="font-display font-black text-2xl mt-6">{r.role}</div>
                <p className={`text-sm mt-3 leading-relaxed ${r.tone === "amber" ? "text-graphite/80" : "text-white/70"}`}>
                  {r.body}
                </p>
                <div className="mt-6 font-mono text-xs tracking-[0.2em] uppercase">
                  {r.tone === "amber" ? "Primary tenant" : "Platform role"}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SavvySpark corporate */}
      <section id="about" className="bg-concrete border-b border-graphite/15" data-testid="savvyspark-section">
        <div className="max-w-6xl mx-auto px-6 py-24">
          <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-amber-construction font-bold mb-4">
            File 05 / Company
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
            <div className="lg:col-span-7">
              <img
                src="https://customer-assets.emergentagent.com/job_construct-hub-357/artifacts/aps478ky_logo.png"
                alt="SavvySpark"
                className="h-16 w-auto mb-8 logo-blend-light"
                data-testid="about-savvyspark-logo"
              />
              <h2 className="font-display text-[2.5rem] md:text-6xl font-black tracking-[-0.02em] leading-[1.02] text-graphite">
                Software for the people who{" "}
                <span className="font-serif-accent text-rust">actually</span>{" "}
                build things.
              </h2>
              <p className="mt-8 text-lg text-graphite/70 leading-[1.7] max-w-xl">
                SavvySpark Solutions Private Limited builds operational software for India's
                construction and real-estate industry. Savvy is our flagship product —
                a tenant-aware portal that connects developers, site teams, and buyers
                in a single transparent workspace.
              </p>
            </div>

            <aside className="lg:col-span-5 lg:pl-10 lg:border-l lg:border-graphite/20">
              <div className="space-y-8">
                <div data-testid="contact-address">
                  <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-amber-construction font-bold mb-2">
                    Registered office
                  </div>
                  <div className="text-graphite text-[15px] leading-relaxed">
                    18M, Emerald Tower-P,<br />
                    Olympia Opaline OMR,<br />
                    Navalur, Chennai 600130,<br />
                    Tamil Nadu, India
                  </div>
                </div>

                <div data-testid="contact-phone">
                  <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-amber-construction font-bold mb-2">
                    Sales & support
                  </div>
                  <a href="tel:+919382750006" className="text-graphite text-lg hover:text-brand-600 transition-colors block font-bold">
                    +91 93827 50006
                  </a>
                  <div className="text-rebar text-xs mt-1 font-mono">
                    Mon–Fri · 10:00–19:00 IST
                  </div>
                </div>

                <div data-testid="contact-email">
                  <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-amber-construction font-bold mb-2">
                    Email
                  </div>
                  <a href="mailto:info@savvyspark.in" className="text-graphite text-lg hover:text-brand-600 transition-colors block font-bold">
                    info@savvyspark.in
                  </a>
                </div>

                <div data-testid="contact-web" className="flex items-start justify-between gap-4 pt-2 border-t border-graphite/20">
                  <div>
                    <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-amber-construction font-bold mb-2">
                      Web
                    </div>
                    <a href="https://www.savvyspark.in" className="text-graphite text-lg hover:text-brand-600 transition-colors font-bold">
                      www.savvyspark.in
                    </a>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-amber-construction font-bold mb-2">
                      CIN
                    </div>
                    <div className="text-rebar text-xs font-mono">
                      U46909TN2024<br />PTC170039
                    </div>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* DEMO section — dark graphite with amber */}
      <section id="demo" className="bg-graphite text-white border-b border-graphite" data-testid="demo-section">
        <div className="max-w-6xl mx-auto px-6 py-24 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-7">
            <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-amber-construction font-bold mb-6">
              File 06 / See it live
            </div>
            <h2 className="font-display text-[2.5rem] md:text-6xl font-black tracking-[-0.02em] leading-[1.02]">
              Book a{" "}
              <span className="font-serif-accent text-amber-construction">private</span>
              <br />
              Savvy walkthrough.
            </h2>
            <p className="mt-8 text-lg text-white/60 leading-[1.7] max-w-xl">
              Tell us about your projects and we'll tailor a 30-minute demo for
              your team — no slide decks, just the product.
            </p>
            <ul className="mt-10 space-y-0 text-[15px] max-w-xl border-t border-white/10">
              {[
                "Response within one business day.",
                "Walkthrough tailored to your portfolio size.",
                "Optional sandbox tenant for 14 days.",
              ].map((txt, i) => (
                <li
                  key={i}
                  className="flex items-start gap-6 py-5 border-b border-white/10"
                >
                  <span className="text-amber-construction font-mono text-xs font-bold tracking-wider pt-1">
                    0{i + 1}
                  </span>
                  <span className="text-white/90">{txt}</span>
                </li>
              ))}
            </ul>
          </div>

          <aside className="lg:col-span-5">
            <div className="bg-concrete text-graphite p-10 relative overflow-hidden" data-testid="demo-cta-card">
              {/* Hazard tape top */}
              <div className="absolute top-0 left-0 right-0 h-1.5 hazard-tape" aria-hidden="true" />

              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-amber-construction font-bold mb-2 mt-2">
                Demo request
              </div>
              <div className="font-display font-black text-[2rem] leading-[1.05] text-graphite">
                Book a Savvy<br />walkthrough.
              </div>
              <div className="mt-6 grid grid-cols-3 gap-0 border-y border-graphite/20 py-4 text-center">
                <div>
                  <div className="font-display font-black text-xl text-graphite">30</div>
                  <div className="font-mono text-[9px] tracking-[0.18em] uppercase text-rebar mt-1">Minutes</div>
                </div>
                <div className="border-l border-graphite/20">
                  <div className="font-display font-black text-xl text-graphite">0</div>
                  <div className="font-mono text-[9px] tracking-[0.18em] uppercase text-rebar mt-1">Slide decks</div>
                </div>
                <div className="border-l border-graphite/20">
                  <div className="font-display font-black text-xl text-graphite">14</div>
                  <div className="font-mono text-[9px] tracking-[0.18em] uppercase text-rebar mt-1">Sandbox days</div>
                </div>
              </div>
              <button
                onClick={() => setDemoOpen(true)}
                data-testid="about-request-demo-btn"
                className="mt-6 w-full bg-graphite hover:bg-amber-construction hover:text-graphite text-white font-bold text-sm tracking-wider uppercase py-4 px-6 flex items-center justify-between gap-2 transition-colors group"
              >
                <span>Request a demo</span>
                <ArrowUpRight size={16} className="group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
              </button>
              <div className="mt-4 text-xs text-rebar">
                Prefer email?{" "}
                <a href="mailto:info@savvyspark.in" className="text-brand-600 hover:text-brand-700 underline underline-offset-2 font-bold">
                  info@savvyspark.in
                </a>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* Corporate partner strip — cream, where logo can blend cleanly */}
      <div className="bg-concrete border-y border-graphite/15" data-testid="corporate-strip">
        <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col sm:flex-row items-center justify-center gap-4">
          <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-rebar">
            Powered by
          </div>
          <img
            src="https://customer-assets.emergentagent.com/job_construct-hub-357/artifacts/aps478ky_logo.png"
            alt="SavvySpark Solutions Private Limited"
            className="h-14 w-auto logo-blend-light"
            data-testid="landing-savvyspark-logo"
          />
        </div>
      </div>

      {/* FOOTER — graphite */}
      <footer className="bg-graphite text-white/70">
        <div className="max-w-7xl mx-auto px-6 py-14">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
            <div className="md:col-span-6">
              <SavvyLogo variant="dark" size={52} showTagline testId="footer-logo" />
              <p className="mt-5 text-sm text-white/50 leading-relaxed max-w-sm">
                The operating layer between your site, your office, and your
                buyers.
              </p>
            </div>

            <div className="md:col-span-3">
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-amber-construction font-bold mb-3">
                Quick links
              </div>
              <div className="flex flex-col gap-2 text-sm">
                <a href="#modules" className="hover:text-white transition-colors">Modules</a>
                <a href="#how" className="hover:text-white transition-colors">How it works</a>
                <a href="#about" className="hover:text-white transition-colors">About SavvySpark</a>
                <button
                  onClick={() => setDemoOpen(true)}
                  className="text-left hover:text-amber-construction transition-colors"
                  data-testid="footer-demo-btn"
                >
                  Request a demo →
                </button>
                <Link to="/login" className="hover:text-white transition-colors">Sign in</Link>
              </div>
            </div>

            <div className="md:col-span-3">
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-amber-construction font-bold mb-3">
                Reach us
              </div>
              <div className="text-sm space-y-1.5">
                <a href="mailto:info@savvyspark.in" className="block hover:text-white transition-colors font-mono">
                  info@savvyspark.in
                </a>
                <a href="tel:+919382750006" className="block hover:text-white transition-colors font-mono">
                  +91 93827 50006
                </a>
                <a href="https://www.savvyspark.in" className="block hover:text-white transition-colors font-mono">
                  www.savvyspark.in
                </a>
                <div className="text-white/40 text-xs font-mono pt-1">
                  Navalur, Chennai 600130, India
                </div>
              </div>
            </div>
          </div>

          <div className="mt-12 pt-6 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="text-xs font-mono text-white/40 tracking-[0.15em] uppercase">
              © 2026 SavvySpark Solutions Private Limited · All rights reserved
            </div>
            <div className="text-xs font-mono text-white/40">
              CIN: U46909TN2024PTC170039
            </div>
          </div>
        </div>

        {/* Hazard tape — bottom */}
        <div className="h-1.5 hazard-tape" aria-hidden="true" />
      </footer>

      <DemoRequestModal open={demoOpen} onClose={() => setDemoOpen(false)} />
    </div>
  );
}
